import { useEffect, useState } from 'react';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import {
  AnomalyDirection,
  AutomatedTestAnomalyConfig,
  AutomatedTestConfigurationApi,
  TestResult,
  TestConfiguration as TestConfigurationType,
  AnomalyDetectionChart,
  FreshnessAnomalyDetectionChart,
  Period,
} from '@elementary/common-ui/types';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ESTIMATIONS } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import {
  simulateCloudTestResult,
  updateCloudTestConfig,
  getCloudTestResultEstimations,
} from 'shared/services/api';
import { PipelineTaskPerformanceConfigApi, SourceType } from 'shared/types';
import {
  AnomalyChartTestExecutionData,
  AutoFreshnessTestExecutionData,
  TestOverviewExecution,
} from 'shared/types/execution';
import useTestOverviewExecution from 'shared/hooks/useTestOverviewExecution';
import { Status, TestOverview, TestConfiguration } from '../types';
import useTestConfiguration from './useTestConfiguration';

const getTestConfig = (
  isFetching: boolean,
  simulatedTestResult: TestResult | undefined,
  testName: string,
  testSubType: string,
  testConfig: TestConfiguration | undefined,
) => {
  if (isFetching) {
    return undefined;
  }

  if (simulatedTestResult) {
    const sensitivity =
      (simulatedTestResult?.metadata?.configuration as TestConfigurationType)
        ?.sensitivity || 'medium';

    const anomalyDirection =
      ((simulatedTestResult?.metadata?.configuration as TestConfigurationType)
        ?.anomaly_direction as AnomalyDirection) || AnomalyDirection.Both;

    const detectionPeriodCount =
      (simulatedTestResult?.metadata?.configuration as TestConfigurationType)
        ?.test_params?.detection_period?.count || 2;

    const detectionPeriodType =
      (simulatedTestResult?.metadata?.configuration as TestConfigurationType)
        ?.test_params?.detection_period?.period || 'day';

    const severity =
      simulatedTestResult?.metadata?.latest_run_status === 'warn' ? 1 : 10;

    const tags = simulatedTestResult?.metadata?.test_tags || [];
    const owners = simulatedTestResult?.metadata?.test_owners || [];

    if (testName === 'pipeline_task_performance') {
      return {
        type: 'pipeline_task_performance' as const,
        mode: 'anomaly',
        sensitivity,
        anomaly_direction: anomalyDirection,
        severity,
        tags,
        owners,
        muted: false,
      };
    }

    const type =
      testName === 'volume_anomalies' || testSubType === 'content'
        ? 'anomaly_detection'
        : 'freshness_anomaly_detection';

    return {
      type,
      anomaly_config: {
        sensitivity,
        anomaly_direction: anomalyDirection,
        detection_period: {
          period: detectionPeriodType,
          count: detectionPeriodCount,
        },
      },
      severity,
      tags,
      owners,
    };
  }

  return testConfig;
};

const getAnomalyChart = (
  isFetching: boolean,
  simulatedTestResult: TestResult | undefined,
  testName: string,
  testSubType: string,
  latestExecution: TestOverviewExecution | undefined,
) => {
  if (isFetching) {
    return null;
  }

  if (simulatedTestResult) {
    if (testName === 'volume_anomalies' || testSubType === 'content') {
      return {
        metrics: Array.isArray(simulatedTestResult?.test_results?.metrics)
          ? simulatedTestResult?.test_results?.metrics
          : [simulatedTestResult?.test_results?.metrics || {}],
        metricsTitle: simulatedTestResult?.test_results?.display_name,
        failOnZero: simulatedTestResult?.metadata?.test_params?.fail_on_zero,
        anomalyDirection: (
          simulatedTestResult?.metadata?.configuration as TestConfigurationType
        )?.anomaly_direction,
        detectionPeriod: simulatedTestResult?.metadata?.test_params
          .backfill_days
          ? {
              count: simulatedTestResult?.metadata?.test_params.backfill_days,
              period: 'day',
            }
          : simulatedTestResult?.metadata?.test_params.detection_period,
        detectionDelay:
          simulatedTestResult?.metadata?.test_params.detection_delay,
        status: simulatedTestResult?.metadata.latest_run_status as Status,
      } as AnomalyDetectionChart;
    }

    if (testName === 'pipeline_task_performance') {
      return {
        metrics: Array.isArray(simulatedTestResult?.test_results?.metrics)
          ? simulatedTestResult?.test_results?.metrics
          : [simulatedTestResult?.test_results?.metrics || {}],
        metricsTitle: simulatedTestResult?.test_results?.display_name,
        anomalyDirection: (
          simulatedTestResult?.metadata?.configuration as TestConfigurationType
        )?.anomaly_direction,
        detectionPeriod: simulatedTestResult?.metadata?.test_params
          .backfill_days
          ? {
              count: simulatedTestResult?.metadata?.test_params.backfill_days,
              period: 'day',
            }
          : simulatedTestResult?.metadata?.test_params.detection_period,
        detectionDelay:
          simulatedTestResult?.metadata?.test_params.detection_delay,
        status: simulatedTestResult?.metadata.latest_run_status as Status,
      } as AnomalyDetectionChart;
    }

    if (testName === 'freshness_anomalies') {
      return {
        metrics: Array.isArray(simulatedTestResult?.test_results?.metrics)
          ? simulatedTestResult?.test_results?.metrics
          : [simulatedTestResult?.test_results?.metrics || {}],
        status: simulatedTestResult?.metadata.latest_run_status as Status,
      } as FreshnessAnomalyDetectionChart;
    }
  }

  if (testName === 'pipeline_task_performance') {
    const latestExecutionData =
      latestExecution?.data as AnomalyChartTestExecutionData;
    return {
      metrics: Array.isArray(latestExecutionData?.metrics)
        ? latestExecutionData?.metrics
        : [latestExecutionData?.metrics || {}],
      metricsTitle: latestExecutionData?.metricsTitle,
      anomalyDirection: latestExecutionData?.anomalyDirection,
      detectionPeriod: latestExecutionData?.detectionPeriod,
      detectionDelay: latestExecutionData?.detectionDelay,
    } as AnomalyDetectionChart;
  }

  if (testName === 'volume_anomalies' || testSubType === 'content') {
    const latestExecutionData =
      latestExecution?.data as AnomalyChartTestExecutionData;
    return {
      metrics: Array.isArray(latestExecutionData?.metrics)
        ? latestExecutionData?.metrics
        : [latestExecutionData?.metrics || {}],
      metricsTitle: latestExecutionData?.metricsTitle,
      failOnZero: latestExecutionData?.failOnZero,
      anomalyDirection: latestExecutionData?.anomalyDirection,
      detectionPeriod: latestExecutionData?.detectionPeriod,
      detectionDelay: latestExecutionData?.detectionDelay,
    } as AnomalyDetectionChart;
  }

  if (testName === 'freshness_anomalies') {
    const latestExecutionData =
      latestExecution?.data as AutoFreshnessTestExecutionData;
    return {
      metrics: Array.isArray(latestExecutionData?.metrics)
        ? latestExecutionData?.metrics
        : [latestExecutionData?.metrics || {}],
      status: latestExecution?.status,
    } as FreshnessAnomalyDetectionChart;
  }

  return null;
};

function useAnomalyDetectionTestConfiguration(testOverview: TestOverview) {
  const selectedEnvId = useSelectedEnv();
  const { selectedId } = useSidePanelParams();
  const [simulatedTestResult, setSimulatedTestResult] = useState<TestResult>();

  const latestExecutionId = testOverview.execution?.id;

  const {
    testConfigData,
    isTestConfigLoading,
    refetch: refetchTestConfiguration,
  } = useTestConfiguration({ testId: testOverview.execution?.testId });
  const { data: latestExecution, isFetching: latestExecutionIsFetching } =
    useTestOverviewExecution<TestOverviewExecution>(
      latestExecutionId,
      SourceType.TEST,
    );

  const isFetching = isTestConfigLoading || latestExecutionIsFetching;

  const { data: estimatedSensitivityRange } = useQuery(
    [ESTIMATIONS, latestExecutionId],
    () =>
      getCloudTestResultEstimations(selectedEnvId!, latestExecutionId, [
        'low',
        'medium',
        'high',
      ]),
    {
      enabled:
        isElementaryCloudTest(testOverview.test.type) &&
        testOverview.test.name === 'freshness_anomalies',
    },
  );

  const { mutate: updateTestConfig, isLoading: isUpdating } = useMutation(
    (
      formData:
        | AutomatedTestConfigurationApi
        | PipelineTaskPerformanceConfigApi,
    ) =>
      updateCloudTestConfig(
        selectedEnvId!,
        selectedId!,
        formData as AutomatedTestConfigurationApi,
      ),
  );

  const { mutate: simulate, isLoading: isSimulating } = useMutation(
    (config: AutomatedTestAnomalyConfig) =>
      simulateCloudTestResult(selectedEnvId!, latestExecutionId, config),
    { onSuccess: (res) => setSimulatedTestResult(res) },
  );

  const testConfig = getTestConfig(
    isFetching,
    simulatedTestResult,
    testOverview.test.name,
    testOverview.execution.subType,
    testConfigData?.test?.config,
  );

  const savedTrainingPeriod: Period | undefined = (() => {
    const config = testConfigData?.test?.config;
    if (
      config?.type === 'anomaly_detection' ||
      config?.type === 'freshness_anomaly_detection'
    ) {
      return config.anomaly_config.training_period;
    }
    return undefined;
  })();

  const anomalyChart = getAnomalyChart(
    isFetching,
    simulatedTestResult,
    testOverview.test.name,
    testOverview.execution.subType,
    latestExecution,
  );

  useEffect(() => {
    setSimulatedTestResult(undefined);
  }, [latestExecutionId]);

  return {
    testConfig,
    refetchTestConfiguration,
    anomalyChart,
    updateTestConfig,
    simulate,
    isFetching,
    isUpdating,
    isSimulating,
    estimatedSensitivityRange,
    savedTrainingPeriod,
  };
}

export default useAnomalyDetectionTestConfiguration;
